import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DialogRef } from '@ngneat/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { fromEvent, Subscription } from 'rxjs';
import { OFFLINE_MODAL_RECONNECT_CLOSE_REASON } from '@app/core/actions/environment.actions';
import { SmashMessageModalComponent } from '../smash-message-modal/smash-message-modal.component';

/**
 * The "we lost your connection, the language could not be loaded" modal, built on the generic
 * SmashMessageModalComponent shell. The literal translation keys live here with the `| translate`
 * pipe, so ngx-translate-extract picks them up and `npm run translate` syncs them with the
 * spreadsheet like every other key. Opened by EnvironmentEffects on SelectLanguageFailure.
 *
 * Auto-closes when the browser regains connectivity (see ModalChunkLoadErrorComponent for the
 * rationale): a blocking "connection lost" overlay must not survive a reconnection and leave the
 * user stuck online behind it.
 */
@Component({
    selector: 'app-modal-language-load-error',
    standalone: true,
    imports: [SmashMessageModalComponent, TranslateModule],
    templateUrl: './modal-language-load-error.component.html',
})
export class ModalLanguageLoadErrorComponent implements OnInit, OnDestroy {
    private onlineSubscription?: Subscription;

    constructor(
        public ref: DialogRef,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    ngOnInit(): void {
        const view = this.document.defaultView;
        if (view) {
            // Close with the 'online' reason so the opening effect knows this was the reconnect
            // auto-close (which completes the switch), NOT a user dismissal (which cancels it).
            this.onlineSubscription = fromEvent(view, 'online').subscribe(() => this.ref.close(OFFLINE_MODAL_RECONNECT_CLOSE_REASON));
        }
    }

    ngOnDestroy(): void {
        this.onlineSubscription?.unsubscribe();
    }
}
